import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationDropdown from './NavigationDropdown';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_PORTFOLIO_CURRENCY } from '../graphql/mutations/portfolios';
import { GET_PORTFOLIOS } from '../graphql/queries/portfolioSummary';
import { GET_TOKEN_WITH_POSITION } from '../graphql/queries/tokens';
import client from '../services/api';
import { SUPPORTED_CURRENCIES } from '../utils/currencies';
import Jump from './Jump';
import NotificationsBell from './NotificationsBell';
import RealtimeIndicator from './RealtimeIndicator';
import { isStockNavRouteActive, isStocksRoute, STOCK_NAV_ROUTES, STOCKS_ROUTE_PREFIX } from '../constants/routes';
import StockJump from './StockJump';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPortfolio, selectPortfolio } = usePortfolioContext();
  const [updateCurrency] = useMutation(UPDATE_PORTFOLIO_CURRENCY, {
    update(cache, { data }) {
      const updated = data?.updatePortfolioCurrency?.portfolio;
      if (!updated?.id || !updated?.currency) return;
      try {
        const existing = cache.readQuery({ query: GET_PORTFOLIOS });
        if (!existing?.portfolios?.length) return;
        const portfolios = existing.portfolios.map((p) =>
          p.id === updated.id ? { ...p, currency: updated.currency } : p
        );
        cache.writeQuery({ query: GET_PORTFOLIOS, data: { portfolios } });
      } catch (_) {
        // Cache may not be populated yet
      }
    },
  });

  // Detect if we are on a token page like /c/:coingeckoSlug
  const tokenMatch = /^\/c\/([^/]+)/.exec(location.pathname || '');
  const coingeckoSlug = tokenMatch ? tokenMatch[1] : null;
  const isOnTokenPage = Boolean(coingeckoSlug);
  const isStocksMode = isStocksRoute(location.pathname);
  const dashboardPath = isStocksMode ? STOCKS_ROUTE_PREFIX : '/';

  // Read token-with-position from Apollo cache only (no network)
  const { data: tokenPageData } = useQuery(GET_TOKEN_WITH_POSITION, {
    variables: coingeckoSlug && currentPortfolio?.id ? { coingeckoSlug, portfolioId: currentPortfolio.id } : undefined,
    skip: !isOnTokenPage || !currentPortfolio?.id,
    fetchPolicy: 'cache-only',
    nextFetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: false,
    returnPartialData: true,
  });

  const hasPositionForToken = (tokenPageData?.tokenWithPosition?.position?.transactions?.length ?? 0) > 0;

  const headerRoutes = isStocksMode
    ? STOCK_NAV_ROUTES
    : [
        { path: '/', label: 'Dashboard', icon: '📊' },
        { path: '/positions', label: 'Positions', icon: '💼' },
        { path: '/allocation-planner', label: 'Planner', icon: '📈' },
        { path: '/transactions', label: 'Transactions', icon: '📝' },
        { path: '/strategies', label: 'Strategies', icon: '📊' },
        { path: '/imports', label: 'Tasks', icon: '⬆️' },
        // { path: '/exits', label: 'Exits', icon: '🎯' },
      ];

  return (
    <div className="bg-[rgb(var(--color-background))] px-2 sm:px-5 py-1">
      <div className="w-full flex justify-between items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-[minmax(0,auto),1fr] items-center gap-x-2 sm:gap-x-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <img 
                src="/pepe_moon.png" 
                alt="Moonready Logo" 
                className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 cursor-pointer shrink-0"
                onClick={() => navigate(dashboardPath)}
              />
              <span
                className="inline-block truncate text-lg sm:text-xl font-semibold tracking-tight scale-x-90 -translate-y-0.5 origin-left text-gray-800 dark:text-gray-100 cursor-pointer select-none"
                onClick={() => navigate(dashboardPath)}
              >
                MoonReady
              </span>
            </div>
            {/* Header Navigation Links in same row as logo/title */}
            <div className="hidden md:flex items-center gap-2">
              {headerRoutes.map((route) => (
                <button
                  key={route.path}
                  onClick={() => navigate(route.path)}
                  className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                    (isStocksMode
                      ? isStockNavRouteActive(route.path, location.pathname)
                      : route.path === '/positions'
                      ? (location.pathname === route.path || (isOnTokenPage && hasPositionForToken))
                      : route.path === '/imports'
                        ? (location.pathname === '/imports' || location.pathname.startsWith('/imports/'))
                        : route.path === '/transactions'
                          ? (location.pathname === '/transactions' || location.pathname.startsWith('/transactions/'))
                          : location.pathname === route.path)
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {/* <span className="text-base">{route.icon}</span> */}
                  <span>{route.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Realtime connection indicator */}
          <RealtimeIndicator />
          {/* Currency switcher */}
          {currentPortfolio && (
            <select
              className="w-16 sm:w-auto px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
              value={currentPortfolio.currency || 'USD'}
              onChange={async (e) => {
                const newCur = e.target.value;
                try {
                  const { data } = await updateCurrency({ variables: { id: currentPortfolio.id, currency: newCur } });
                  const updated = data?.updatePortfolioCurrency?.portfolio;
                  if (updated) {
                    selectPortfolio({ ...currentPortfolio, currency: updated.currency });
                    try { localStorage.setItem('selectedPortfolioCurrency', updated.currency); } catch (_) {}
                    try {
                      await client.refetchQueries({
                        include: [
                          'GetPortfolios', 'GetPortfolioSummary', 'GetPositions', 'GetTokens', 'GetTransactions',
                          'GetStockDashboard', 'GetStockPositionsList', 'GetStockPosition',
                          'GetStockTransactions', 'GetStockPortfolioTransactions', 'GetStockChart', 'GetStockDetails',
                        ],
                      });
                    } catch (_) {}
                  }
                } catch (err) {
                  console.error('Failed to update currency', err);
                }
              }}
            >
              {SUPPORTED_CURRENCIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          <div className="block">
            {isStocksMode ? <StockJump /> : <Jump />}
          </div>
          <NotificationsBell />
          <NavigationDropdown />
        </div>
      </div>
    </div>
  );
};

export default Header; 